import {
  Balance,
  MoreVert,
  NotificationsActive,
  Payments,
} from "@mui/icons-material";
import { Button, Divider, IconButton } from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import LoadingOverlay from "../../../components/LoadingOverlay/LoadingOverlay";
import MoreMenu from "../../../components/MenuMore/MoreMenu";
import { useModal } from "../../../providers/ModalProvider";
import { Erc20TokenInfo } from "../../../types";
import { formatNumberWithSeparator } from "../../../utils/number-utils";
import WidgetList from "../../dashboard/components/WidgetList";
import {
  selectCommodityExchangeTokens,
  selectCommodityTokenMarketplaceContractAddress,
} from "../../settings/settings.selectors";
import { useTokenTransferModal } from "../providers/TokenTransferModalProvider";
import { getRwaContractBalancesAction } from "../treasury.effects";
import {
  selectIsGetRwaContractBalancesPending,
  selectRwaContractBalances,
} from "../treasury.selectors";
import {
  RwaContractBalances as RwaContractBalancesType,
  RwaTokenBalance,
} from "../types/rwa-contract-balances.type";
import RWATokenUpdateBalanceForm from "./RwaTokenUpdateBalanceForm";

const renderHeader = (commodityExchangeTokens: Erc20TokenInfo[]) => (
  <div className="flex justify-between items-end">
    <span className="py-1 pl-1">Token</span>
    <div className="flex">
      <div className="self-end py-1 w-[140px] text-right mr-2">
        Total Supply
      </div>
      <div className="self-end py-1 w-[140px] text-right mr-2">Total Sold</div>
      <div className="self-end py-1 w-[140px] text-right mr-10">
        Available to Buy
      </div>
      {commodityExchangeTokens?.map((token) => (
        <div
          key={token.symbol}
          className="w-[200px] flex flex-col items-center [&:not(:last-of-type)]:border-r"
        >
          <div className="pb-1 flex items-center gap-1 font-semibold">
            {token.imageUrl && <img className="w-6 h-6" src={token.imageUrl} />}
            {token.symbol}
          </div>
          <div className="w-full flex">
            <span className="flex-1 py-1 text-center border-r">Treasury</span>
            <span className="flex-1 py-1 text-center">Commission</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const renderItem = (
  item: RwaTokenBalance,
  commodityExchangeTokens: Erc20TokenInfo[]
) => {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="pl-1">{item.rwaToken.symbol}</span>
      <div className="flex">
        <div className="py-1 w-[140px] text-right mr-2">
          {formatNumberWithSeparator(item.rwaToken.totalSupply, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
        <div className="py-1 w-[140px] text-right mr-2">
          {formatNumberWithSeparator(
            item.rwaToken.totalSupply - item.rwaToken.amountAvailableToBuy,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}
        </div>
        <div className="py-1 w-[140px] text-right mr-10">
          {formatNumberWithSeparator(item.rwaToken.amountAvailableToBuy, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
        {commodityExchangeTokens?.map((token) => (
          <div
            key={token.symbol}
            className="w-[200px] flex [&:not(:last-of-type)]:border-r"
          >
            <span className="flex-1 py-2 text-center border-r">
              {formatNumberWithSeparator(item[token.symbol].treasuryFormatted, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
            <span className="flex-1 py-2 text-center">
              {formatNumberWithSeparator(
                item[token.symbol].commissionFormatted,
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const renderSummary = (
  rwaContractBalances: RwaContractBalancesType,
  commodityExchangeTokens: Erc20TokenInfo[]
) => {
  return (
    <div className="flex flex-col text-sm">
      <div className="flex justify-between items-center border-b">
        <span className="pl-1 opacity-60 text-xs uppercase">
          Virtual Balance
        </span>
        <div className="flex">
          {commodityExchangeTokens?.map((token) => (
            <div
              key={token.symbol}
              className="w-[200px] py-2 flex justify-center items-center gap-1 [&:not(:last-of-type)]:border-r"
            >
              {formatNumberWithSeparator(
                rwaContractBalances?.totalVirtualBalances[token.symbol].total,
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }
              )}{" "}
              {rwaContractBalances?.totalVirtualBalances[token.symbol].diff ? (
                <span className="text-xs text-red-600">
                  (
                  {rwaContractBalances?.totalVirtualBalances[token.symbol]
                    .diff > 0
                    ? "+"
                    : "-"}
                  {formatNumberWithSeparator(
                    Math.abs(
                      rwaContractBalances?.totalVirtualBalances[token.symbol]
                        .diff
                    ),
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}
                  )
                </span>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between items-center">
        <span className="pl-1 opacity-60 text-xs uppercase">
          Actual Balance
        </span>
        <div className="flex">
          {commodityExchangeTokens?.map((token) => (
            <div
              key={token.symbol}
              className="w-[200px] py-2 text-center [&:not(:last-of-type)]:border-r"
            >
              {formatNumberWithSeparator(
                rwaContractBalances?.totalActualBalances[token.symbol],
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function RwaContractBalances() {
  const rwaContractBalances = useSelector(selectRwaContractBalances);
  const isPending = useSelector(selectIsGetRwaContractBalancesPending);
  const commodityExchangeTokens = useSelector(selectCommodityExchangeTokens);
  const commodityTokenMarketplaceContractAddress = useSelector(
    selectCommodityTokenMarketplaceContractAddress
  );
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const { openModal } = useModal();
  const { openTransferModal } = useTokenTransferModal();
  const dispatch = useDispatch();

  const handleUpdateBalance = async () => {
    closeMenu();
    if (
      await openModal((props) => (
        <RWATokenUpdateBalanceForm
          {...props}
          rwaContractBalances={rwaContractBalances}
          commodityExchangeTokens={commodityExchangeTokens}
        />
      ))
    ) {
      dispatch(getRwaContractBalancesAction());
    }
  };

  const handleDeposit = async () => {
    closeMenu();
    if (
      await openTransferModal({ to: commodityTokenMarketplaceContractAddress })
    ) {
      dispatch(getRwaContractBalancesAction());
    }
  };

  const showMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  return (
    <LoadingOverlay loading={isPending}>
      <div className="w-full h-full p-widget">
        <div className="WidgetHeader  flex justify-between items-center">
          Gateway Contract Balances
          <IconButton size="small" onClick={showMenu}>
            <MoreVert />
          </IconButton>
        </div>
        <div className="mt-4">
          <WidgetList
            items={rwaContractBalances?.rwaBalances}
            getKey={(item) => item.rwaToken.symbol}
            itemsContainerClassName="max-h-[176px] overflow-y-auto"
            headerClassName="p-0"
            itemClassName="p-0"
            renderHeader={() => renderHeader(commodityExchangeTokens)}
            renderItem={(item) => renderItem(item, commodityExchangeTokens)}
          />
          {rwaContractBalances?.rwaBalances &&
            renderSummary(rwaContractBalances, commodityExchangeTokens)}
        </div>
      </div>
      <MoreMenu anchorEl={menuAnchorEl} onClose={closeMenu}>
        <div className="flex flex-col px-1">
          <Button
            className="justify-start"
            variant="text"
            color="info"
            startIcon={<Balance />}
            onClick={handleUpdateBalance}
          >
            Update Balance
          </Button>
          <Divider />
          <Button
            className="justify-start"
            variant="text"
            color="darkGold"
            startIcon={<Payments />}
            onClick={handleDeposit}
          >
            Deposit
          </Button>
          <Divider />
          <Button
            className="justify-start"
            variant="text"
            color="warning"
            startIcon={<NotificationsActive />}
            onClick={() => {}}
          >
            Balance Notifier
          </Button>
        </div>
      </MoreMenu>
    </LoadingOverlay>
  );
}
